import { useAuth0 } from "ct-auth"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  bulkCustomizeScenarios,
  createScenarioFromTemplate,
  deleteTest,
  downloadScenarioAsRawFile,
  getBulkCustomizeStatus,
  getScenarios,
  importScenarioFromFile,
  updateTest,
} from "./data"
import { isManager, userOrg } from "./authUtils"

const comingSoonScenarioItems = []

const trimmedCommaSeparatedListItems = (commaDelimitedList) => {
  return commaDelimitedList ? commaDelimitedList.split(",").map((item) => item.trim()) : []
}

const gameScenarioLabelFromData = (scenarioData) => {
  let label = ""
  // Check for flags in the scenario "Info" (metadata)
  if (scenarioData?.info?.["Beta"]) {
    label = "Beta"
  } else if (scenarioData?.info?.["Coming Soon"]) {
    label = "Coming Soon"
  }
  return label
}

export const gameScenarioListFromData = (scenarioData) => {
  return {
    scenario_id: scenarioData?.name ?? "",
    icon_name: scenarioData?.info?.Icon ? [scenarioData.info.Icon] : [],
    scenario_name: scenarioData?.info?.Name ?? "",
    scenario_label: gameScenarioLabelFromData(scenarioData),
    scenario_details: {
      messaging_type: trimmedCommaSeparatedListItems(scenarioData?.info?.Products),
      user_type: scenarioData?.roles ?? [],
      organisation_type: trimmedCommaSeparatedListItems(scenarioData?.info?.Sectors),
      info: scenarioData?.info?.Description ?? "",
    },
  }
}

export const gameScenarioListFromDataList = (scenarioDataList) => {
  return (scenarioDataList ?? [])
    .filter((scenarioData) => scenarioData.archived !== true) // Hide 'archived' scenarios
    .map((scenarioData) => gameScenarioListFromData(scenarioData))
    .concat(comingSoonScenarioItems) // Render the coming soon items also (for now)
}

const VARIABLE_CODE_PRIVATE = "PRIVATE"
const VARIABLE_CODE_RUNBOOK_GENERATOR = "RUNBOOK_GENERATOR"
const VARIABLE_CODE_REPORT_GENERATOR = "REPORT_GENERATOR"

const PRIVATE_VAR_CODES_LOWERCASE_SET = new Set(
  [VARIABLE_CODE_PRIVATE, VARIABLE_CODE_REPORT_GENERATOR, VARIABLE_CODE_RUNBOOK_GENERATOR].map((code) => code.toLowerCase())
)

export const gameScenarioVariableIsPrivate = (variable) => {
  if (variable) {
    const lowercasedVariableCodesSet = new Set(
      variable.codes
        ?.split(",")
        .map((code) => code.trim())
        .map((code) => code.toLowerCase()) ?? []
    )
    return PRIVATE_VAR_CODES_LOWERCASE_SET.intersection(lowercasedVariableCodesSet).size > 0
  }
  return false
}

export const useScenarioApis = Object.freeze({
  SCENARIO_LIST: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: [`SCENARIO_LIST-${user?.sub}`],
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await getScenarios(accessToken, user.sub)
        }
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })
  },

  COPY_SCENARIO: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async (scenarioId) => {
        console.log("COPY_SCENARIO called with scenarioId:", scenarioId)
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          const newScenario = await createScenarioFromTemplate(accessToken, user.sub, { simulationId: scenarioId })
          if (!newScenario?.id) throw new Error("Failed to copy scenario.")
          return newScenario
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
      },
    })
  },

  DOWNLOAD_SCENARIO: ({ scenarioId, fileName }) => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: [user?.sub, `DOWNLOAD_SCENARIO-${scenarioId ?? undefined}`],
      queryFn: async () => {
        if (user?.sub && scenarioId) {
          const accessToken = await getAccessTokenSilently()
          const result = await downloadScenarioAsRawFile(accessToken, user.sub, {
            scenarioId: scenarioId,
            fileName: fileName,
          })
          return result ?? null
        }
        return null
      },
      enabled: !!(scenarioId && user?.sub),
      staleTime: 1000, // 5 minutes
    })
  },

  BULK_CUSTOMIZE: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async (bulkCustomizeData) => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          const { result, status } = await bulkCustomizeScenarios(accessToken, user.sub, bulkCustomizeData)
          if (status !== 200) {
            throw new Error(result?.error || "Failed to bulk customize scenarios")
          }
          return result
        }
        throw new Error("User not authenticated")
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
        queryClient.invalidateQueries([`BULK_CUSTOMIZE_STATUS-${user?.sub}`])
      },
    })
  },
  BULK_CUSTOMIZE_STATUS: () => {
    const { getAccessTokenSilently, user } = useAuth0()
    return useQuery({
      queryKey: [`BULK_CUSTOMIZE_STATUS-${user?.sub}`],
      queryFn: async () => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          // Get orgId from user claims
          const orgId = userOrg(user)
          const { result, status } = await getBulkCustomizeStatus(accessToken, user.sub, { orgId })
          if (status !== 200) {
            throw new Error(result?.error || "Failed to get bulk customize status")
          }
          return result
        }
        throw new Error("User not authenticated")
      },
      enabled: !!user?.sub && isManager(user),
    })
  },

  DELETE_SCENARIO: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ name }) => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await deleteTest(accessToken, user.sub, { name })
        }
        throw new Error("User not authenticated")
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
      },
    })
  },

  // Import a scenario from an XLSX file (manager-only; server enforces the role gate).
  // `ownerOrg` = null/undefined → global (no owner); a specific org id → owned by that org.
  IMPORT_SCENARIO: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ file, test, ownerOrg }) => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          return await importScenarioFromFile(accessToken, user.sub, { file, test, ownerOrg })
        }
        throw new Error("User not authenticated")
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
      },
    })
  },
  // Archive / unarchive a scenario by flipping its `archived` flag via updateTest (PATCH).
  // Server is ownership-gated; the UI additionally gates the action with canEditScenarioClient.
  // updateTest returns null on a non-200, so treat that as a failure.
  ARCHIVE_SCENARIO: () => {
    const queryClient = useQueryClient()
    const { getAccessTokenSilently, user } = useAuth0()
    return useMutation({
      mutationFn: async ({ name, archived }) => {
        if (user?.sub) {
          const accessToken = await getAccessTokenSilently()
          const result = await updateTest(accessToken, user.sub, { name, archived })
          if (!result) throw new Error("Failed to update scenario archive state.")
          return result
        }
        throw new Error("User not authenticated")
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`SCENARIO_LIST-${user?.sub}`])
      },
    })
  },
})
